import { Link, useParams } from 'react-router-dom'
import { Building2, Globe, Landmark, Users } from 'lucide-react'
import {
  Badge,
  combineQueryStates,
  LinkChip,
  NotFoundView,
  PageHeader,
  QueryErrorPanel,
  SectionHeader,
  Tag,
} from '@loop3/ui'
import { AskLupeButton } from '@/components/ask-lupe-button'
import { DetailSkeleton } from '@/components/explorer-detail'
import { EntityGrid } from '@/components/entity-card'
import { CompanyProfileSection } from '@/components/explorer/profile-section'
import { useCompany } from '@/hooks/useCompany'

/** "client", "2025-01-14" → "Client since 2025-01". */
function relationshipLabel(type: string, startedAt: string | null): string {
  const name = type.replace(/_/g, ' ')
  const label = name.charAt(0).toUpperCase() + name.slice(1)
  return startedAt ? `${label} since ${startedAt.slice(0, 7)}` : label
}

/** One company: domains, relationships, the people we know there and its client record. */
export function CompanyPage() {
  const { slug } = useParams<{ slug: string }>()
  const company = useCompany(slug)
  const { status, retry } = combineQueryStates([company])

  if (status === 'error')
    return (
      <div className="mx-auto max-w-7xl pb-16">
        <QueryErrorPanel onRetry={retry} />
      </div>
    )
  if (status === 'pending') return <DetailSkeleton />
  if (!company.data)
    return (
      <NotFoundView
        title="Company not found"
        description="No company in the universe has this address."
        homeLink={<Link to="/explorer" />}
        homeLabel="Back to Explorer"
      />
    )

  const c = company.data
  return (
    <div className="mx-auto max-w-7xl pb-16">
      <PageHeader
        className="mt-2"
        eyebrow="Company"
        title={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-[var(--l3-violet-soft)] text-[var(--l3-violet)]">
              <Landmark className="size-5" aria-hidden />
            </span>
            <span className="min-w-0 [overflow-wrap:anywhere]">{c.name}</span>
            {c.kind && (
              <Badge variant="outline" className="font-mono font-normal">
                {c.kind}
              </Badge>
            )}
          </span>
        }
        subtitle={c.description ?? undefined}
        actions={
          <AskLupeButton question={`What do we know about ${c.name}?`} />
        }
      >
        {(c.relationships.length > 0 || c.domains.length > 0) && (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            {c.relationships.map((r) => (
              <Tag key={`${r.type}:${r.started_at}`} variant="pulse">
                {relationshipLabel(r.type, r.started_at)}
              </Tag>
            ))}
            {c.domains.map((d) => (
              <LinkChip
                key={d}
                href={`https://${d}`}
                icon={
                  <Globe className="size-3.5 shrink-0 opacity-60" aria-hidden />
                }
                label={d}
                className="min-w-0 max-w-full [overflow-wrap:anywhere]"
              />
            ))}
          </div>
        )}
      </PageHeader>

      <CompanyProfileSection profile={c.profile} />

      {c.client && (
        <section className="mb-8">
          <SectionHeader icon={Building2} label="Loop3 client" />
          <EntityGrid
            items={[
              {
                kind: 'client',
                id: c.client.prefix,
                title: c.client.name,
                subtitle: c.client.prefix,
              },
            ]}
          />
        </section>
      )}

      <section>
        <SectionHeader
          icon={Users}
          label="People we know"
          meta={c.people.length || undefined}
        />
        {c.people.length > 0 ? (
          <EntityGrid
            items={c.people.map((p) => ({
              kind: 'person',
              id: p.id,
              title: p.full_name,
              subtitle: p.role ?? undefined,
            }))}
          />
        ) : (
          <p className="border-t border-border py-6 text-center text-sm opacity-60">
            No one on record here yet.
          </p>
        )}
      </section>
    </div>
  )
}
